import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { AiOutlineClose, AiOutlineWarning } from "react-icons/ai";
import {
  deleteCalendar,
  getAllCalendars,
} from "../../../services/calendarServices";

const Calendars = () => {
  const [calendarList, setCalendarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toggle, setToggle] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [currentCalendar, setCurrentCalendar] = useState(null);
  const [confirmError, setConfirmError] = useState("");

  const fetchCalendars = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAllCalendars();
      const data = Array.isArray(response.data) ? response.data : [];
      setCalendarList(
        [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      );
    } catch (err) {
      setError(
        "Unable to load calendars. Make sure the backend /api/calendar endpoint is available."
      );
      setCalendarList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendars();
  }, []);

  const handleDelete = (calendar) => {
    setToggle(true);
    setCurrentCalendar(calendar);
    setConfirm("");
    setConfirmError("");
  };

  const handleDeleteCalendar = async () => {
    if (!currentCalendar) return;
    if (confirm !== currentCalendar.year) {
      setConfirmError("Please type the academic year to confirm deletion.");
      return;
    }
    try {
      await deleteCalendar(currentCalendar._id);
      setToggle(false);
      fetchCalendars();
    } catch (err) {
      setConfirmError("Failed to delete calendar. Please try again.");
    }
  };

  const totalEntries = (calendar) =>
    (calendar.terms || []).reduce(
      (sum, term) => sum + (term.calendar || []).length,
      0
    );

  return (
    <div className="py-5 px-4 bg-slate-50 shadow-lg mx-2 rounded-md">
      <div className="bg-slate-200 space-x-4 py-3 px-3 mx-2 flex justify-between w-full items-center">
        <input
          type="text"
          placeholder="Search Here"
          className="relative w-full py-2 px-3 outline-none rounded-md"
        />
        <Link
          to="/calendar-entry"
          className="w-fit font-medium bg-blue-700 text-white hover:bg-blue-900 px-3 py-1 rounded-lg shadow-lg whitespace-nowrap"
        >
          Add academic year
        </Link>
      </div>

      {loading && (
        <p className="text-center py-10 text-gray-500">Loading calendars...</p>
      )}
      {error && (
        <p className="text-center py-6 text-red-600 text-sm px-4">{error}</p>
      )}

      {!loading && (
        <div className="overflow-x-auto relative shadow-md mt-4">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs font-bold text-gray-700 uppercase bg-gray-50">
              <tr>
                <th className="py-3 px-6">Order</th>
                <th className="py-3 px-6">Academic Year</th>
                <th className="py-3 px-6">Semesters</th>
                <th className="py-3 px-6">Entries</th>
                <th className="py-3 px-6">Status</th>
                <th className="py-3 px-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {calendarList.map((calendar) => (
                <tr key={calendar._id} className="bg-white border-b">
                  <td className="py-4 px-6">{calendar.sortOrder ?? "-"}</td>
                  <td className="py-4 px-6 font-medium text-gray-900">
                    {calendar.year}
                  </td>
                  <td className="py-4 px-6">
                    {(calendar.terms || []).length}
                  </td>
                  <td className="py-4 px-6">{totalEntries(calendar)}</td>
                  <td className="py-4 px-6 capitalize">
                    {calendar.status || "published"}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <Link
                        to="/edit-calendar"
                        state={{ calendar }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FaEdit size={18} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(calendar)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <MdDelete size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!calendarList.length && !error && (
                <tr>
                  <td colSpan={6} className="py-8 px-6 text-center text-gray-500">
                    No academic calendars yet. Click &quot;Add academic year&quot; to
                    create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div
        className={`fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm flex justify-center items-center z-30 ${
          toggle ? "" : "hidden"
        }`}
      >
        <div className="flex flex-col bg-white border shadow-sm rounded-xl max-w-lg w-full mx-4">
          <div className="flex justify-between items-center py-2.5 px-4 border-b">
            <h3 className="font-bold text-gray-800">Delete Calendar</h3>
            <button
              type="button"
              onClick={() => setToggle(false)}
              className="text-gray-500 hover:text-gray-400"
            >
              <AiOutlineClose className="text-xl" />
            </button>
          </div>
          <div className="p-4 flex flex-row gap-4">
            <AiOutlineWarning className="text-5xl text-red-500 flex-shrink-0" />
            <p className="text-gray-800">
              Delete academic year{" "}
              <span className="font-semibold">
                {currentCalendar?.year}
              </span>
              ? Type the year below to confirm.
            </p>
          </div>
          <div className="px-4 pb-2">
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type academic year to confirm"
              className="block bg-white w-full border border-slate-300 rounded-md py-2 px-3 text-sm"
            />
            {confirmError && (
              <p className="text-red-500 text-sm mt-2">{confirmError}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 py-3 px-4 border-t">
            <button
              type="button"
              onClick={() => setToggle(false)}
              className="px-4 py-2 bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteCalendar}
              className="px-4 py-2 bg-red-600 text-white rounded-md"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendars;
